import { ArrowLeft, Save, Loader2, Factory, PackageCheck } from 'lucide-react';
import type { NavigateFunction } from 'react-router-dom';
import { PRIORITIES, type Product, type BOM, type SalesOrder, type User, type FormData } from './ProductionOrderCreatePage';
interface Props {
 navigate:NavigateFunction;products:Product[];salesOrders:SalesOrder[];users:User[];form:FormData;fieldErrors:Record<string,string>;selectedBom:BOM|null;
 inputCls:string;inputErrCls:string;labelCls:string;updateField:(field:keyof FormData,value:string)=>void;handleSubmit:()=>void;saving:boolean;error:string|null;loading:boolean;loadError:boolean;profileManager:boolean;retry:()=>void;
 title:string;submitLabel:string;productLocked:boolean;onCancel?:()=>void;
}
export function ProductionOrderFormView({navigate,products,salesOrders,users,form,fieldErrors,selectedBom,inputCls,inputErrCls,labelCls,updateField,handleSubmit,saving,error,loading,loadError,profileManager,retry,title,submitLabel,productLocked,onCancel}:Props) {
  const leave = () => (onCancel ? onCancel() : navigate('/manufacturing/dashboard'));
  return (
    <div className="mx-auto max-w-3xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={leave}
          className="rounded-lg p-1.5 hover:bg-[var(--bg-inset)] transition-colors"
        >
          <ArrowLeft size={20} className="text-[var(--text-muted)]" />
        </button>
        <div className="flex items-center gap-2">
          <Factory size={22} className="text-[var(--saffron-600)]" />
          <div>
            <h1 className="text-xl font-bold text-[var(--text-primary)]">
              {title}
            </h1>
            <p className="text-xs text-[var(--text-muted)]">
              Schedule a manufacturing run from a Bill of Materials
            </p>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div role="alert" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error} {loadError && <button onClick={retry}>Retry</button>}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[var(--text-muted)]">
          <Loader2 size={20} className="animate-spin mr-2" />
          Loading products & BOMs...
        </div>
      ) : loadError ? null : products.length === 0 ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-6 py-8 text-center">
          <PackageCheck size={32} className="mx-auto mb-3 text-amber-400" />
          <p className="text-sm font-medium text-amber-400">No products with BOMs found</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            Create a Bill of Materials for your products first, then come back to create a production order.
          </p>
        </div>
      ) : (
      <div className="space-y-6">
        {/* Required fields */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h2 className="mb-4 text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            Order details
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Product */}
            <div>
              <label className={labelCls}>
                Product <span className="text-red-400">*</span>
              </label>
              <select
                value={form.productId}
                onChange={e => updateField('productId', e.target.value)}
                disabled={productLocked}
                className={fieldErrors.productId ? inputErrCls : inputCls}
              >
                <option value="">Select Product</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {productLocked && (
                <p className="mt-1 text-xs text-[var(--text-muted)]">Job cards or serial numbers already exist for this order. Make a new order for this product.</p>
              )}
              {fieldErrors.productId && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.productId}</p>
              )}
            </div>

            {/* BOM — auto-selected display (no dropdown) */}
            <div>
              <label className={labelCls}>
                Bill of Materials
              </label>
              {!form.productId ? (
                <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-muted)]">
                  Select a product first
                </div>
              ) : selectedBom ? (
                <div className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/5 px-3 py-2">
                  <PackageCheck size={14} className="text-green-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                      {selectedBom.name}
                    </p>
                    {selectedBom._count?.components && (
                      <p className="text-xs text-[var(--text-muted)]">
                        {selectedBom._count.components} components
                        {selectedBom.version ? ` · v${selectedBom.version}` : ''}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-400">
                  No BOM found for this product
                </div>
              )}
              {fieldErrors.bomId && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.bomId}</p>
              )}
            </div>

            {/* Target Qty */}
            <div>
              <label className={labelCls}>
                Target Quantity <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={form.targetQty}
                onChange={e => updateField('targetQty', e.target.value)}
                placeholder="e.g. 100"
                className={fieldErrors.targetQty ? inputErrCls : inputCls}
              />
              {fieldErrors.targetQty && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.targetQty}</p>
              )}
            </div>

            {/* Priority */}
            <div>
              <label className={labelCls}>Priority</label>
              <div className="relative">
                <select
                  value={form.priority}
                  onChange={e => updateField('priority', e.target.value)}
                  className={inputCls}
                >
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <span
                  className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full ${
                    PRIORITIES.find(p => p.value === form.priority)?.dot ?? 'bg-blue-400'
                  }`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scheduling */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h2 className="mb-4 text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            Scheduling
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Planned Start */}
            <div>
              <label className={labelCls}>Planned Start</label>
              <input
                type="date"
                value={form.plannedStart}
                onChange={e => updateField('plannedStart', e.target.value)}
                className={inputCls}
              />
            </div>

            {/* Planned End */}
            <div>
              <label className={labelCls}>Planned End</label>
              <input
                type="date"
                value={form.plannedEnd}
                onChange={e => updateField('plannedEnd', e.target.value)}
                min={form.plannedStart || undefined}
                className={fieldErrors.plannedEnd ? inputErrCls : inputCls}
              />
              {fieldErrors.plannedEnd && (
                <p className="mt-1 text-xs text-red-400">{fieldErrors.plannedEnd}</p>
              )}
            </div>
          </div>
        </div>

        {!profileManager && <>
        {/* Optional linking */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h2 className="mb-4 text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            Linking and assignment
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Sales Order */}
            <div>
              <label className={labelCls}>Sales Order</label>
              <select
                value={form.salesOrderId}
                onChange={e => updateField('salesOrderId', e.target.value)}
                className={inputCls}
              >
                <option value="">None</option>
                {salesOrders.map(so => (
                  <option key={so.id} value={so.id}>
                    {so.number} ({so.status})
                  </option>
                ))}
              </select>
            </div>

            {/* Assigned To */}
            <div>
              <label className={labelCls}>Assigned To</label>
              <select
                value={form.assignedTo}
                onChange={e => updateField('assignedTo', e.target.value)}
                className={inputCls}
              >
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name || u.email}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        </>}
        {/* Notes */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h2 className="mb-4 text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            Notes
          </h2>
          <textarea
            value={form.notes}
            onChange={e => updateField('notes', e.target.value)}
            rows={4}
            placeholder="Any special instructions or remarks..."
            className={`${inputCls} resize-y`}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pb-8">
          <button
            onClick={leave}
            className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-5 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {submitLabel}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
